/**
 * Ticketing API Service — thin SDK HTTP calls (same tenant/token interceptors as app SDK).
 */
import { httpGet, httpPost, httpPut, httpDelete, httpPatch, sdk } from '../api/sdk-instance.js';
import { uploadAxiosConfig } from '@repo/sdk/utils/upload-axios-config.js';

const BASE = '/tickets';

export const ticketApi = {
    // ── List & Stats ────────────────────────────────────────────
    getAll: (params = {}) => httpGet(BASE, params),
    getMyPending: () => httpGet(`${BASE}/my-pending`),
    getPool: (params = {}) => httpGet(`${BASE}/pool`, params),
    getStats: () => httpGet(`${BASE}/stats`),
    getById: (id) => httpGet(`${BASE}/${id}`),

    getPartyTemplate: (params = {}) => httpGet(`${BASE}/party-template`, params),
    getParty: (id) => httpGet(`${BASE}/${id}/party`),
    syncParty: (id, body) => httpPut(`${BASE}/${id}/party`, body),

    // ── CRUD ────────────────────────────────────────────────────
    create: (data) => httpPost(BASE, data),
    update: (id, data) => httpPut(`${BASE}/${id}`, data),
    delete: (id) => httpDelete(`${BASE}/${id}`),
    batchDelete: (ids) => httpPost(`${BASE}/batch-delete`, { ids }),
    postItsmLinks: (id, data) => httpPost(`${BASE}/${id}/itsm-links`, data),
    patchItsmCatalog: (id, data) => httpPatch(`${BASE}/${id}/itsm-catalog`, data),

    // ── Linked corrective actions (incident parity) ───────────
    getRelatedActions: (id, params = {}) => httpGet(`${BASE}/${id}/actions`, params),
    getLinkableActions: (id, params = {}) => httpGet(`${BASE}/${id}/actions/linkable`, params),
    linkActions: (id, actionIds, extra = {}) => httpPost(`${BASE}/${id}/actions`, { actionIds, ...extra }),
    unlinkActions: (id, actionIds) => httpPost(`${BASE}/${id}/actions/unlink`, { actionIds }),
    unlinkAction: (id, actionId) => httpDelete(`${BASE}/${id}/actions/${actionId}`),
    reorderActions: (id, { actionIds }) => httpPut(`${BASE}/${id}/actions/reorder`, { actionIds }),

    putTicketTasks: (id, data) => httpPut(`${BASE}/${id}/tasks`, data),
    postTicketTask: (id, payload) => httpPost(`${BASE}/${id}/tasks`, payload),
    patchTicketTask: (id, taskId, payload) => httpPut(`${BASE}/${id}/tasks/${taskId}`, payload),
    deleteTicketTask: (id, taskId) => httpDelete(`${BASE}/${id}/tasks/${taskId}`),
    transitionTicketTask: (id, taskId, payload) =>
        httpPost(`${BASE}/${id}/tasks/${taskId}/transition`, payload || {}),
    listSubtaskComments: (id, taskId, params = {}) =>
        httpGet(`${BASE}/${id}/tasks/${taskId}/comments`, params),
    addSubtaskComment: (id, taskId, data) =>
        httpPost(`${BASE}/${id}/tasks/${taskId}/comments`, data),

    // ── State Machine Actions ───────────────────────────────────
    submit: (id) => httpPost(`${BASE}/${id}/submit`),
    assign: (id, data) => httpPost(`${BASE}/${id}/assign`, data),
    patchFormData: (id, data) => httpPatch(`${BASE}/${id}/form-data`, data),
    claim: (id) => httpPost(`${BASE}/${id}/claim`),
    acceptAssignment: (id) => httpPost(`${BASE}/${id}/accept-assignment`),
    rejectAssignment: (id, data) => httpPost(`${BASE}/${id}/reject-assignment`, data),
    resolve: (id, data) => httpPost(`${BASE}/${id}/resolve`, data),
    close: (id, data) => httpPost(`${BASE}/${id}/close`, data),
    cancel: (id, data) => httpPost(`${BASE}/${id}/cancel`, data),

    // ── Watchers ────────────────────────────────────────────────
    addWatcher: (id, data) => httpPost(`${BASE}/${id}/watch`, data),
    removeWatcher: (id, data) => httpPost(`${BASE}/${id}/unwatch`, data),

    // ── Bulk Operations ─────────────────────────────────────────
    bulkAction: (data) => httpPost(`${BASE}/bulk-action`, data),

    // ── Sub-tickets & Merge ───────────────────────────────────
    createSubTicket: (id, data) => httpPost(`${BASE}/${id}/sub-ticket`, data),
    merge: (id, data) => httpPost(`${BASE}/${id}/merge`, data),
    uploadAttachment: (id, file) => {
        const formData = new FormData();
        formData.append('file', file);
        return sdk.client.post(
            `${BASE}/${id}/attachments`,
            formData,
            uploadAxiosConfig({ headers: { 'Content-Type': 'multipart/form-data' } }),
        ).then((res) => res.data);
    },
    deleteAttachment: (id, attachmentId) => httpDelete(`${BASE}/${id}/attachments/${attachmentId}`),
    addRelatedTicket: (id, relatedTicketId) => httpPost(`${BASE}/${id}/related`, { relatedTicketId }),
    removeRelatedTicket: (id, relatedId) => httpDelete(`${BASE}/${id}/related/${relatedId}`),

    // ── Search & Export ─────────────────────────────────────────
    searchSimilar: (query) => httpGet(`${BASE}/search/similar`, { query }),
    exportTickets: (params = {}) => httpGet(`${BASE}/export`, params),

    // ── Report ──────────────────────────────────────────────────
    getReportUrl: (id) => httpGet(`${BASE}/${id}/report`),

    // ── Comments ────────────────────────────────────────────────
    getComments: (ticketId, params = {}) =>
        httpGet(`/ticket-comments`, { limit: 300, ...params, ticket: ticketId }),
    addComment: (data) => httpPost(`/ticket-comments`, data),
    updateComment: (commentId, data) => httpPut(`/ticket-comments/${commentId}`, data),
    deleteComment: (commentId) => httpDelete(`/ticket-comments/${commentId}`)
};

// ── Template API ─────────────────────────────────────────────────────────────
export const ticketTemplateApi = {
    getAll: (params = {}) => httpGet('/ticket-templates', params),
    getForCreate: () => httpGet('/ticket-templates/for-create'),
    getReportLinkOptions: () => httpGet('/ticket-templates/report-link-options'),
    getWorkflowTemplateOptions: (params = {}) => httpGet('/ticket-templates/workflow-template-options', params),
    getById: (id) => httpGet(`/ticket-templates/${id}`),
    create: (data) => httpPost('/ticket-templates', data),
    update: (id, data) => httpPut(`/ticket-templates/${id}`, data),
    delete: (id) => httpDelete(`/ticket-templates/${id}`),
    publish: (id) => httpPost(`/ticket-templates/${id}/publish`),
    archive: (id) => httpPost(`/ticket-templates/${id}/archive`),
    createReportDesignerCapability: (id) => httpPost(`/ticket-templates/${id}/report-designer-capability`),
    getReportFields: (id, params = {}) => httpGet(`/ticket-templates/${id}/report-fields`, params),
};

// ── Category API ─────────────────────────────────────────────────────────────
export const ticketCategoryApi = {
    getAll: (params = {}) => httpGet('/ticket-categories', params),
    getById: (id) => httpGet(`/ticket-categories/${id}`),
    create: (data) => httpPost('/ticket-categories', data),
    update: (id, data) => httpPut(`/ticket-categories/${id}`, data),
    delete: (id) => httpDelete(`/ticket-categories/${id}`)
};

// ── SLA API ──────────────────────────────────────────────────────────────────
export const ticketSlaApi = {
    getAll: (params = {}) => httpGet('/ticket-slas', params),
    getById: (id) => httpGet(`/ticket-slas/${id}`),
    create: (data) => httpPost('/ticket-slas', data),
    update: (id, data) => httpPut(`/ticket-slas/${id}`, data),
    delete: (id) => httpDelete(`/ticket-slas/${id}`)
};

// ── Config API (tenant-level singleton) ──────────────────────────────────────
export const ticketConfigApi = {
    get: () => httpGet('/ticket-config'),
    update: (data) => httpPut('/ticket-config', data)
};

export const ticketRecurrenceApi = {
    getAll: (params = {}) => httpGet('/ticket-recurrences', params),
    getById: (id) => httpGet(`/ticket-recurrences/${id}`),
    create: (data) => httpPost('/ticket-recurrences', data),
    update: (id, data) => httpPut(`/ticket-recurrences/${id}`, data),
    delete: (id) => httpDelete(`/ticket-recurrences/${id}`),
};
